import type { ClassValue } from 'clsx';

import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * A list of decimal prefixes.
 * @deprecated Use DecimalPrefix from packages/ui/src/lib/file-size.ts instead.
 */
export type DecimalPrefix
    = | 'B'
        | 'kB'
        | 'MB'
        | 'GB'
        | 'TB'
        | 'PB'
        | 'EB'
        | 'ZB'
        | 'YB';

/**
 * A utility function to merge Tailwind CSS classes with the clsx utility.
 * @param inputs The classes to merge.
 * @returns The merged classes.
 */
export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

/**
 * A utility function to format a number as a human-readable file size.
 * @param size The size of the file in bytes.
 * @returns The formatted file size.
 * @deprecated Use FileSize class instead.
 */
export function humanFileSize(size: number) {
    const i = size === 0 ? 0 : Math.floor(Math.log(size) / Math.log(1024));
    const value = size / 1024 ** i;
    const prefix: DecimalPrefix[] = [
        'B',
        'kB',
        'MB',
        'GB',
        'TB',
        'PB',
        'EB',
        'ZB',
        'YB',
    ];

    return `${value.toFixed(2)} ${prefix[i]}`;
}

/**
 * A utility function to format a file name.
 * @param fileName The file to format.
 * @returns The formatted file name.
 */
export function formatFileName(fileName: string) {
    const array = fileName.split('.');
    const extension = array.pop();
    const name = array.join('.');

    if (name.length > 20)
        return `${name.slice(0, 20)}...${extension}`;

    return `${name}.${extension}`;
}

export function blobToFile(blob: Blob, fileName: string, fileType?: string) {
    const file = new File([blob], fileName, { type: fileType });

    return file;
}

export async function wait(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

export function booleanDefaultValue(
    value: boolean | null | undefined,
    defaultValue: boolean | null = null,
): boolean | null {
    return typeof value == 'boolean' ? value : defaultValue;
}

/**
 * A utility function to convert a hex color code to RGB.
 * @param hex The hex color code to convert.
 * @returns An object containing the RGB values of the color.
 */
export function hexToRgba(hex: string, alpha: number = 1) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? `rgba(${Number.parseInt(result[1], 16)}, ${Number.parseInt(result[2], 16)}, ${Number.parseInt(result[3], 16)}, ${alpha})` : null;
}

// device maps
const iosDeviceMapping = new Map([
    ['320x480', 'IPhone 4S, 4, 3GS, 3G, 1st gen'],
    ['320x568', 'IPhone 5, SE 1st Gen,5C, 5S'],
    ['375x667', 'IPhone SE 2nd Gen, 6, 6S, 7, 8'],
    ['375x812', 'IPhone X, XS, 11 Pro, 12 Mini, 13 Mini'],
    ['390x844', 'IPhone 13, 13 Pro, 12, 12 Pro'],
    ['414x736', 'IPhone 8+'],
    ['414x896', 'IPhone 11, XR, XS Max, 11 Pro Max'],
    ['428x926', 'IPhone 13 Pro Max, 12 Pro Max'],
    ['476x847', 'IPhone 7+, 6+, 6S+'],
    ['744x1133', 'IPad Mini 6th Gen'],
    [
        '768x1024',
        'IPad Mini (5th Gen), IPad (1-6th Gen), iPad Pro (1st Gen 9.7), Ipad Mini (1-4), IPad Air(1-2)  ',
    ],
    ['810x1080', 'IPad 7-9th Gen'],
    ['820x1180', 'iPad Air (4th gen)'],
    ['834x1194', 'iPad Pro (3-5th Gen 11)'],
    ['834x1112', 'iPad Air (3rd gen), iPad Pro (2nd gen 10.5)'],
    ['1024x1366', 'iPad Pro (1-5th Gen 12.9)'],
]);

const desktopDeviceMapping = new Map([
    ['Win32', 'Windows'],
    ['Linux', 'Linux'],
    ['MacIntel', 'Mac OS'],
]);

// get device name for android
function getAndroidDeviceName() {
    const androidUserAgentString = window.navigator.userAgent.slice(window.navigator.userAgent.indexOf('Android'));
    const androidDeviceName = androidUserAgentString.slice(androidUserAgentString.indexOf('; ') + 1, androidUserAgentString.indexOf(')'));
    if (androidDeviceName) {
        return androidDeviceName.trim().split(' ')[0];
    }

    return 'Android';
};

// get device name for ios
function getIosDeviceName() {
    const screenResolution = `${window.screen.width}x${window.screen.height}`;
    const device = iosDeviceMapping.get(screenResolution);
    if (device) {
        return device;
    }
    return 'Iphone';
};

// get device name for desktop
function getDesktopDeviceName() {
    const platform = navigator?.platform || 'unknown';
    return desktopDeviceMapping.get(platform) ?? 'Unknown';
};

// get device name utility
export function getDeviceName(): string {
    // check if mobile device
    const isMobileDevice = window.navigator.userAgent
        .toLowerCase()
        .includes('mobi');

    if (isMobileDevice) {
        if (window.navigator.userAgent.includes('Android')) {
            return getAndroidDeviceName();
        }
        else {
            return getIosDeviceName();
        }
    }
    else {
        return getDesktopDeviceName();
    }
}

/**
 * Generates a simple, non-cryptographic hash from a string.
 * It's fast and produces a URL-friendly "c-hash" string.
 * @param str The string to hash.
 * @param seed A seed to alter the hash. Defaults to 0.
 * @returns A hash string (e.g., "c-123456789").
 */
export function simpleHash(str: string, seed = 0): string {
    let h1 = 0xDEADBEEF ^ seed;
    let h2 = 0x41C6CE57 ^ seed;
    for (let i = 0, ch; i < str.length; i++) {
        ch = str.charCodeAt(i);
        h1 = Math.imul(h1 ^ ch, 2654435761);
        h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
    h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
    h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);

    // Prepending "c-" ensures the result is always a valid CSS selector
    // and avoids accidental collisions with purely numeric IDs.
    return `c-${(4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36)}`;
}

/**
 * Converts an array of strings to a record of strings with hashed keys.
 * @param arr The array of strings to convert.
 * @returns A record of strings with hashed keys.
 */
export function arrayToHashedRecord(arr: string[]): Record<string, string> {
    const record: Record<string, string> = {};
    for (const value of arr) {
        record[simpleHash(value)] = value;
    }
    return record;
}
